import { useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Twitter, Linkedin, Instagram } from "lucide-react";
import { Helmet } from "react-helmet";
import logo1 from "../images/QS_Logo.png";

export default function Footer() {
  const [showModal, setShowModal] = useState(null); // "privacy" | "terms" | serviceName | null
  const businessNumber = "918106938625"; // Business number

  // Updated Product & Services with detailed info
  const services = [
    {
      name: "Custom Documentation Automation",
      details: `EMAN & ASYCUDA format solutions
Automate customs documentation with scalable, country-specific compliance. Streamline processes, reduce manual errors, and ensure regulatory compliance across borders.`,
    },
    {
      name: "Operations Management Software",
      details: `Sales, Purchase, Delivery Orders & Invoicing
Streamline and digitize day-to-day operations with integrated or stand-alone modules. Improve efficiency, tracking, and reporting for all operations.`,
    },
    {
      name: "Freight Forwarding Software",
      details: `Booking, Consolidation & Billing
End-to-end freight lifecycle management for air, sea, and land. Optimize cargo handling, track shipments, and automate billing and reporting processes.`,
    },
    {
      name: "AI Chatbots & Virtual Assistants",
      details: `Intelligent Customer Support
Instant shipment status, documentation queries, and self-service support. Enhance customer experience with AI-powered interactions and 24/7 assistance.`,
    },
  ];

  return (
    <>
      {/* SEO / Structured Data */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Quadrangle Solutions",
            url: "https://www.quadranglesolutions.com",
            logo: "https://www.quadranglesolutions.com/logo.png",
            sameAs: [
              "https://www.facebook.com/quadranglesolutions",
              "https://www.linkedin.com/company/quadranglesolutions/",
              "https://twitter.com/quadranglesolutions",
              "https://www.instagram.com/quadranglesolutions",
            ],
            contactPoint: [
              {
                "@type": "ContactPoint",
                telephone: `+${businessNumber}`,
                contactType: "customer service",
                areaServed: "IN",
              },
            ],
          })}
        </script>
      </Helmet>

      <footer className="relative bg-gradient-to-b from-white via-[#e6fdf6] to-[#00cc88] py-16 text-gray-800 font-sans">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            {/* Brand Section */}
            <div className="space-y-4">
              <Link to="/">
                <div className="rounded-md">
                  <img
                    src={logo1}
                    alt="Quadrangle Solutions Logo"
                    className="h-[9rem] w-[14rem] object-contain"
                  />
                </div>
              </Link>
            </div>

            {/* Services Section */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-5">
                Product & Services
              </h3>
              <ul className="space-y-3">
                {services.map((item) => (
                  <li key={item.name}>
                    <button
                      onClick={() => setShowModal(item.name)}
                      className="text-gray-700 hover:text-[#008060] transition-colors duration-300 text-sm font-medium"
                    >
                      {item.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company Section */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-5">
                Company
              </h3>
              <ul className="space-y-3">
                {[
                  { name: "About Us", href: "/about" },
                  { name: "Our Clients", href: "/ourclients" },
                  { name: "Careers", href: "/careers" },
                  { name: "Contact", href: "/contact" },
                ].map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className="text-gray-700 hover:text-[#008060] transition-colors duration-300 text-sm font-medium"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Connect Section */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-5">
                Connect
              </h3>
              <div className="flex space-x-4">
                {[
                  {
                    name: "LinkedIn",
                    href: "https://www.linkedin.com/company/quadranglesolutions/",
                    icon: <Linkedin />,
                  },
                  {
                    name: "Twitter",
                    href: "https://twitter.com",
                    icon: <Twitter />,
                  },
                  {
                    name: "Facebook",
                    href: "https://facebook.com",
                    icon: <Facebook />,
                  },
                  {
                    name: "Instagram",
                    href: "https://instagram.com",
                    icon: <Instagram />,
                  },
                ].map((social) => (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative flex items-center justify-center w-12 h-12 bg-white rounded-full shadow-lg hover:bg-[#00cc88] transition-all duration-300 ease-in-out"
                    aria-label={social.name}
                  >
                    <span className="text-[#00cc88] group-hover:text-white transition-colors duration-300">
                      {social.icon}
                    </span>
                    <span className="absolute -top-10 text-xs text-gray-700 bg-white px-2 py-1 rounded shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      {social.name}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="border-t border-[#00cc88]/40 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-700 text-sm font-light">
              &copy; {new Date().getFullYear()} Quadrangle Solutions. All rights
              reserved.
            </p>
            <div className="mt-4 md:mt-0 flex space-x-6">
              <button
                onClick={() => setShowModal("privacy")}
                className="text-gray-700 hover:text-[#008060] transition-colors duration-300 text-sm font-medium"
              >
                Privacy Policy
              </button>
              <button
                onClick={() => setShowModal("terms")}
                className="text-gray-700 hover:text-[#008060] transition-colors duration-300 text-sm font-medium"
              >
                Terms of Service
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-lg w-full relative">
            <button
              onClick={() => setShowModal(null)}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-800"
            >
              ✕
            </button>

            {/* Modal Content */}
            {showModal === "privacy" || showModal === "terms" ? (
              <>
                <h2 className="text-xl font-semibold mb-4 text-[#00cc88]">
                  {showModal === "privacy"
                    ? "Privacy Policy"
                    : "Terms of Service"}
                </h2>
                <div className="text-gray-600 text-sm space-y-3 max-h-64 overflow-y-auto">
                  {showModal === "privacy" ? (
                    <>
                      <p>
                        We respect your privacy. Your information is never
                        shared with third parties without consent.
                      </p>
                      <p>
                        By using our website, you agree to how we collect and
                        use data as outlined here.
                      </p>
                    </>
                  ) : (
                    <>
                      <p>
                        By using our services, you agree to follow all
                        applicable laws and regulations.
                      </p>
                      <p>
                        We may update these terms from time to time. Continued
                        use means you accept the latest version.
                      </p>
                    </>
                  )}
                </div>
              </>
            ) : (
              <>
                <h2 className="text-xl font-semibold mb-4 text-[#00cc88]">
                  {showModal}
                </h2>
                <div className="text-gray-600 text-sm space-y-2 max-h-64 overflow-y-auto">
                  {services
                    .find((s) => s.name === showModal)
                    ?.details.split("\n")
                    .map((line, idx) => (
                      <p key={idx}>{line}</p>
                    ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
